import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  BarChart3,
  Check,
  Compass,
  FileCheck2,
  Gauge,
  Globe2,
  Layers,
  Leaf,
  LineChart,
  ShieldCheck,
  Sparkles,
  Sun,
  Wind,
  Zap,
} from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";
import heroImg from "@/assets/images/hero-industry.jpg";
import solarImg from "@/assets/images/solar-farm.jpg";
import windImg from "@/assets/images/wind-turbines.jpg";
import lithiumImg from "@/assets/images/lithium-lab.jpg";
import dashboardImg from "@/assets/images/dashboard.jpg";
import whyEsgImg from "@/assets/images/why-esg.jpg";

const sectorIcons = [Leaf, Wind, Sun, Zap];
const processIcons = [Compass, Layers, LineChart, FileCheck2];

export const HomePage = () => {
  const { t, dict } = useI18n();
  const home = dict.home;
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const validate = () => {
    const e = {};
    if (!name.trim() || name.trim().length < 2) e.name = home.newsletter.error_name;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) e.email = home.newsletter.error_email;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    // Persist locally (frontend only) and navigate to French-only thank you
    try {
      const key = "vexorqa.newsletter.leads";
      const existing = JSON.parse(localStorage.getItem(key) || "[]");
      existing.push({ name: name.trim(), email: email.trim(), ts: new Date().toISOString() });
      localStorage.setItem(key, JSON.stringify(existing));
    } catch (err) {}
    navigate("/thank_you");
  };

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative bg-[#0B1E36] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img
            src={heroImg}
            alt=""
            aria-hidden="true"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B1E36] via-[#0B1E36]/85 to-transparent" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 sm:py-24 lg:py-28 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <span
              data-testid="hero-badge"
              className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-600/15 border border-emerald-400/40 text-emerald-200 text-[11px] uppercase tracking-[0.22em] rounded-sm"
            >
              <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
              {home.hero.badge}
            </span>
            <h1
              data-testid="hero-title"
              className="mt-6 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight"
              style={{ fontFamily: "Plus Jakarta Sans, sans-serif" }}
            >
              {home.hero.title}
            </h1>
            <div className="mt-6 space-y-4 text-slate-200 text-base sm:text-lg max-w-3xl">
              <p>{home.hero.intro_1}</p>
              <p className="text-slate-300">{home.hero.intro_2}</p>
              <p className="text-slate-400">{home.hero.intro_3}</p>
            </div>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                data-testid="hero-cta-primary"
                onClick={() => scrollTo("bulletin")}
                className="inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-6 py-3.5 rounded-sm"
              >
                {home.hero.cta_primary} <ArrowRight className="h-4 w-4" />
              </button>
              <button
                type="button"
                data-testid="hero-cta-secondary"
                onClick={() => scrollTo("circuit")}
                className="inline-flex items-center justify-center gap-2 border border-slate-500 text-slate-100 hover:border-emerald-400 hover:text-emerald-300 text-sm font-semibold px-6 py-3.5 rounded-sm"
              >
                {home.hero.cta_secondary}
              </button>
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="relative bg-white/5 border border-slate-700/70 backdrop-blur-sm rounded-sm p-3">
              <img
                src={dashboardImg}
                alt="Dashboard analytique Vexorqa"
                className="w-full h-auto rounded-sm object-cover aspect-[4/3]"
              />
              <div className="mt-3 flex items-center justify-between px-2 pb-1 text-[11px] text-slate-300">
                <span className="uppercase tracking-widest">Bloomberg-style</span>
                <span className="flex items-center gap-1">
                  <Gauge className="h-3.5 w-3.5 text-emerald-400" /> Realtime
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-white border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10">
          <p className="text-[11px] font-mono uppercase tracking-[0.24em] text-emerald-700">
            {home.stats.title}
          </p>
          <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-6">
            {home.stats.items.map((s, i) => (
              <div
                key={i}
                data-testid={`stat-item-${i}`}
                className="border-l-2 border-emerald-600 pl-4"
              >
                <div className="text-2xl sm:text-3xl font-bold text-[#0B1E36]">
                  {s.value}
                </div>
                <p className="mt-1 text-xs text-slate-600 leading-relaxed">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY */}
      <section id="analyse" className="bg-slate-50 border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 sm:py-20 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <p className="text-[11px] font-mono uppercase tracking-[0.24em] text-emerald-700">
              {home.why.eyebrow}
            </p>
            <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900">
              {home.why.title}
            </h2>
            <div className="mt-6 relative rounded-sm overflow-hidden border border-slate-200">
              <img
                src={whyEsgImg}
                alt="Parc éolien français au lever du soleil — transition énergétique ESG"
                className="w-full h-64 object-cover"
              />
            </div>
          </div>
          <ul className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {home.why.items.map((it, i) => (
              <li
                key={i}
                data-testid={`why-item-${i}`}
                className="bg-white border border-slate-200 p-6 rounded-sm hover:border-emerald-400"
              >
                <span className="flex h-9 w-9 items-center justify-center bg-emerald-100 text-emerald-700 border border-emerald-300 rounded-sm">
                  <Check className="h-4 w-4" strokeWidth={2.6} />
                </span>
                <h3 className="mt-4 text-base font-semibold text-slate-900">{it.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{it.description}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* PROCESS */}
      <section id="circuit" className="bg-white border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="max-w-3xl">
            <p className="text-[11px] font-mono uppercase tracking-[0.24em] text-emerald-700">
              {home.process.eyebrow}
            </p>
            <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900">
              {home.process.title}
            </h2>
            <p className="mt-4 text-slate-600">{home.process.lead}</p>
          </div>
          <ol className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {home.process.steps.map((s, i) => {
              const Icon = processIcons[i] || Compass;
              return (
                <li
                  key={i}
                  data-testid={`process-step-${i}`}
                  className="relative bg-white border border-slate-200 p-6 rounded-sm"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-mono tracking-[0.24em] text-emerald-700">
                      {s.number}
                    </span>
                    <Icon className="h-5 w-5 text-[#0B1E36]" aria-hidden="true" />
                  </div>
                  <h3 className="mt-4 text-base font-semibold text-slate-900">{s.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.description}</p>
                </li>
              );
            })}
          </ol>
        </div>
      </section>

      {/* SECTORS */}
      <section className="bg-[#F8FAFC] border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 sm:py-20 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <p className="text-[11px] font-mono uppercase tracking-[0.24em] text-emerald-700">
              {home.sectors.eyebrow}
            </p>
            <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900">
              {home.sectors.title}
            </h2>
            <div className="mt-6 grid grid-cols-2 gap-3">
              <img
                src={solarImg}
                alt="Ferme solaire en France"
                className="w-full h-32 object-cover rounded-sm border border-slate-200"
              />
              <img
                src={windImg}
                alt="Éoliennes onshore"
                className="w-full h-32 object-cover rounded-sm border border-slate-200"
              />
            </div>
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-4">
            {home.sectors.items.map((it, i) => {
              const Icon = sectorIcons[i] || Leaf;
              return (
                <div
                  key={i}
                  data-testid={`sector-item-${i}`}
                  className="bg-white border border-slate-200 p-6 rounded-sm flex gap-4"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center bg-[#0B1E36] text-emerald-300 rounded-sm">
                    <Icon className="h-5 w-5" />
                  </span>
                  <div>
                    <h3 className="text-base font-semibold text-slate-900">{it.title}</h3>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">{it.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* TRANSPARENCY */}
      <section id="transparence" className="bg-[#0B1E36] text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 sm:py-20 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-6">
            <p className="text-[11px] font-mono uppercase tracking-[0.24em] text-emerald-300">
              {home.transparency.eyebrow}
            </p>
            <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">
              {home.transparency.title}
            </h2>
            <p className="mt-5 text-slate-200 leading-relaxed">
              {home.transparency.paragraph_1}
            </p>
            <p className="mt-4 text-slate-300 leading-relaxed">
              {home.transparency.paragraph_2}
            </p>
          </div>
          <div className="lg:col-span-6 grid gap-4">
            {home.transparency.pillars.map((p, i) => (
              <div
                key={i}
                data-testid={`transparency-pillar-${i}`}
                className="border border-slate-700/80 bg-white/5 p-6 rounded-sm"
              >
                <div className="flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center bg-emerald-600 text-white rounded-sm">
                    {i === 0 ? <ShieldCheck className="h-4 w-4" /> : i === 1 ? <Globe2 className="h-4 w-4" /> : <BarChart3 className="h-4 w-4" />}
                  </span>
                  <h3 className="text-base font-semibold">{p.title}</h3>
                </div>
                <p className="mt-3 text-sm text-slate-300 leading-relaxed">{p.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* NEWSLETTER */}
      <section id="bulletin" className="bg-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5">
              <p className="text-[11px] font-mono uppercase tracking-[0.24em] text-emerald-700">
                {home.newsletter.eyebrow}
              </p>
              <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900">
                {home.newsletter.title}
              </h2>
              <p className="mt-4 text-slate-600 leading-relaxed">{home.newsletter.subtitle}</p>
              <ul className="mt-6 space-y-2">
                {home.newsletter.guarantees.map((g, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                    <Check className="mt-0.5 h-4 w-4 text-emerald-600" strokeWidth={2.6} />
                    <span>{g}</span>
                  </li>
                ))}
              </ul>
            </div>
            <form
              onSubmit={onSubmit}
              noValidate
              data-testid="newsletter-form"
              className="lg:col-span-7 bg-slate-50 border border-slate-200 rounded-sm p-6 sm:p-8"
            >
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="nl-name" className="block text-xs uppercase tracking-widest text-slate-600 font-semibold">
                    {home.newsletter.name_label}
                  </label>
                  <input
                    id="nl-name"
                    type="text"
                    autoComplete="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    data-testid="newsletter-name-input"
                    placeholder={home.newsletter.name_placeholder}
                    className="mt-2 w-full bg-white border border-slate-300 focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 outline-none px-3 py-3 text-sm rounded-sm"
                  />
                  {errors.name && (
                    <p data-testid="newsletter-name-error" className="mt-1 text-xs text-red-600">
                      {errors.name}
                    </p>
                  )}
                </div>
                <div>
                  <label htmlFor="nl-email" className="block text-xs uppercase tracking-widest text-slate-600 font-semibold">
                    {home.newsletter.email_label}
                  </label>
                  <input
                    id="nl-email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="newsletter-email-input"
                    placeholder={home.newsletter.email_placeholder}
                    className="mt-2 w-full bg-white border border-slate-300 focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 outline-none px-3 py-3 text-sm rounded-sm"
                  />
                  {errors.email && (
                    <p data-testid="newsletter-email-error" className="mt-1 text-xs text-red-600">
                      {errors.email}
                    </p>
                  )}
                </div>
              </div>
              <p className="mt-4 text-xs text-slate-500 leading-relaxed">
                {home.newsletter.consent}
              </p>
              <button
                type="submit"
                disabled={submitting}
                data-testid="newsletter-submit"
                className="mt-5 inline-flex items-center gap-2 bg-[#0B1E36] hover:bg-[#162E4D] text-white text-sm font-semibold px-6 py-3.5 rounded-sm disabled:opacity-70"
              >
                {home.newsletter.submit} <ArrowRight className="h-4 w-4" />
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
